import React, { useState, useEffect } from 'react';
import { Card, Table, Form, Button } from 'react-bootstrap';
import Pagination from 'react-js-pagination';
import { useQuery, useMutation } from 'react-query';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { NGANHDAOTAO_API } from '../../../utils/apiUrl';
import { Get, DeleteSingle } from '../../../utils/httpProvider';
import { messageToast, status200, objectShowNumber } from '../../../utils/define';
import { ConfirmModal } from '../../../modules/modal/ConfirmModal';
import NganhDaoTaoModal from './components/NganhDaoTaoModal';

const NganhDaoTao = () => {
    const valueSearch = useSelector((state) => state.search.valueSearch);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [openModal, setOpenModal] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);

    // Delete Confirmation State
    const [idWantDelete, setIdWantDelete] = useState('');
    const [confirmDelMessage, setConfirmDelMessage] = useState('');
    const [openConfirmDeleteModal, setOpenConfirmDeleteModal] = useState(false);

    // --- FETCH DATA ---
    const { data: listData, refetch, isLoading } = useQuery(
        ['nganhDaoTaoList', page, pageSize, valueSearch],
        () => Get(NGANHDAOTAO_API.GET_PAGING, null, {
            pageIndex: page,
            pageSize: pageSize,
            keyword: valueSearch || ''
        }, false),
        { keepPreviousData: true }
    );

    // Reset pagination when search keyword changes
    useEffect(() => {
        setPage(1);
    }, [valueSearch]);

    // Mutation for deleting
    const deleteMutation = useMutation((id) => DeleteSingle(`${NGANHDAOTAO_API.DELETE_ITEM}/${id}`));

    const handleDeleteClick = (id, name) => {
        setIdWantDelete(id);
        setConfirmDelMessage(`Bạn có chắc chắn muốn xóa ngành đào tạo: "${name}"?`);
        setOpenConfirmDeleteModal(true);
    };

    const handleConfirmDelete = () => {
        if (!idWantDelete) return;

        deleteMutation.mutate(idWantDelete, {
            onSuccess: (res) => {
                if (res.status === status200 || res.status === 201 || res === true || res.id) {
                    toast.success(messageToast.messageToastDeleteSuccess);
                    refetch();
                } else {
                    toast.error(messageToast.messageToastDeleteError);
                }
            },
            onError: (err) => {
                toast.error(err?.message || "Có lỗi xảy ra khi xóa.");
            }
        });

        setOpenConfirmDeleteModal(false);
        setIdWantDelete('');
    };

    const items = listData?.data?.$values || listData?.data || listData?.items?.$values || listData?.items || [];
    const totalRecords = listData?.totalRecords || 0;

    return (
        <div className="container-fluid p-4">
            <Card className="shadow-sm border-0" style={{ borderRadius: '12px' }}>
                <Card.Header className="bg-white border-bottom py-3">
                    <div className="d-flex justify-content-between align-items-center">
                        <h5 className="font-weight-bold mb-0 text-primary">
                            <i className="fas fa-stethoscope mr-2"></i>Quản lý Ngành đào tạo
                        </h5>
                        <Button 
                            variant="success" 
                            onClick={() => { setSelectedItem(null); setOpenModal(true); }}
                            style={{ borderRadius: '8px' }}
                        >
                            <i className="fas fa-plus mr-1"></i>Thêm ngành đào tạo
                        </Button>
                    </div>
                </Card.Header>
                <Card.Body>
                    <div className="table-responsive">
                        <Table striped bordered hover className="align-middle">
                            <thead className="thead-light">
                                <tr>
                                    <th style={{ width: '60px' }} className="text-center">STT</th>
                                    <th>Mã ngành đào tạo</th>
                                    <th>Tên ngành đào tạo</th>
                                    <th>Ghi chú</th>
                                    <th style={{ width: '150px' }} className="text-center">Hành động</th>
                                </tr>
                            </thead>
                            <tbody>
                                {isLoading ? (
                                    <tr><td colSpan={5} className="text-center py-4">Đang tải dữ liệu...</td></tr>
                                ) : items.length === 0 ? (
                                    <tr><td colSpan={5} className="text-center py-4 text-muted">Chưa có ngành đào tạo nào</td></tr>
                                ) : (
                                    items.map((item, idx) => (
                                        <tr key={item.id || item.Id}>
                                            <td className="text-center">{(page - 1) * pageSize + idx + 1}</td>
                                            <td className="font-weight-bold text-dark">{item.maNganh || item.MaNganh}</td>
                                            <td>{item.tenNganh || item.TenNganh}</td>
                                            <td className="text-muted">{item.ghiChu || item.GhiChu || '-'}</td>
                                            <td className="text-center">
                                                <Button 
                                                    variant="outline-primary" 
                                                    size="sm" 
                                                    className="mr-2"
                                                    onClick={() => { setSelectedItem(item); setOpenModal(true); }}
                                                    style={{ borderRadius: '6px' }}
                                                >
                                                    <i className="fas fa-edit mr-1"></i>Sửa
                                                </Button>
                                                <Button 
                                                    variant="outline-danger" 
                                                    size="sm"
                                                    onClick={() => handleDeleteClick(item.id || item.Id, item.tenNganh || item.TenNganh)}
                                                    style={{ borderRadius: '6px' }}
                                                >
                                                    <i className="fas fa-trash-alt mr-1"></i>Xóa
                                                </Button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </Table>
                    </div>

                    <div className="d-flex justify-content-between align-items-center mt-3">
                        <Form.Control 
                            as="select" 
                            value={pageSize} 
                            onChange={(e) => setPageSize(Number(e.target.value))} 
                            style={{ width: '100px', borderRadius: '8px' }}
                        >
                            {objectShowNumber.map((opt) => (
                                <option key={opt.value} value={opt.value}>{opt.text}</option>
                            ))}
                        </Form.Control>
                        <Pagination
                            itemClass="page-item"
                            linkClass="page-link"
                            innerClass="pagination pagination-sm no-margin mb-0"
                            activePage={page}
                            itemsCountPerPage={pageSize}
                            totalItemsCount={totalRecords}
                            pageRangeDisplayed={5}
                            onChange={(num) => setPage(num)}
                        />
                    </div>
                </Card.Body>
            </Card>

            <NganhDaoTaoModal 
                isOpen={openModal} 
                handleClose={() => { setOpenModal(false); setSelectedItem(null); }} 
                data={selectedItem} 
                refetch={refetch} 
            />

            <ConfirmModal
                isOpen={openConfirmDeleteModal}
                confirmType="delete"
                handleAccept={handleConfirmDelete}
                handleCancel={() => { setOpenConfirmDeleteModal(false); setIdWantDelete(''); }}
                content={confirmDelMessage}
            />
        </div>
    );
};

export default NganhDaoTao;
